import { useEffect, useRef, useState } from "react";
import { Group, Image as KonvaImage } from "react-konva";
import { loadImage } from "../lib/imageCache.js";
import { segmentUrl } from "../api.js";

// One Konva Group per canvas layer. The group hosts an Image child per
// member, positioned at the member's (dx, dy) — the offset of that
// segment's bbox in the source-image coordinate frame, relative to the
// anchor (members[0]). The group's transform lives on the layer; mirror
// flags compose with scaleX/Y at render time.
//
// Multi-select drag: when this layer is part of a multi-selection, drag
// emits delta updates to its sibling selected nodes so the whole bundle
// moves together.

export default function LayerNode({
  layer,
  isSelected,
  selectedIds,
  onSelect,
  onChange,
  onMultiDragStart,
  onMultiDragMove,
  onMultiDragEnd,
  onAltDuplicate,
  onContextMenu,
  registerNode,
}) {
  const groupRef = useRef(null);
  const [images, setImages] = useState([]);

  // Preload images for all current members.
  useEffect(() => {
    let cancelled = false;
    Promise.all(layer.members.map((m) => loadImage(segmentUrl(m.file)).catch(() => null)))
      .then((imgs) => { if (!cancelled) setImages(imgs); });
    return () => { cancelled = true; };
  }, [layer.members]);

  useEffect(() => {
    if (registerNode) registerNode(layer.id, groupRef.current);
    return () => { if (registerNode) registerNode(layer.id, null); };
  }, [layer.id, registerNode]);

  // Group origin = bbox centroid of all members → rotation/mirror feel
  // natural around the visual center.
  let minDx = 0, minDy = 0, maxRight = 0, maxBottom = 0;
  for (const m of layer.members) {
    if (m.dx < minDx) minDx = m.dx;
    if (m.dy < minDy) minDy = m.dy;
    if (m.dx + m.w > maxRight) maxRight = m.dx + m.w;
    if (m.dy + m.h > maxBottom) maxBottom = m.dy + m.h;
  }
  const cx = (minDx + maxRight) / 2;
  const cy = (minDy + maxBottom) / 2;

  const t = layer.transform;
  const renderScaleX = t.scaleX * (t.mirrorH ? -1 : 1);
  const renderScaleY = t.scaleY * (t.mirrorV ? -1 : 1);

  const isMultiSelected = isSelected && selectedIds && selectedIds.length > 1;
  // We don't track per-node prev positions anymore — Canvas captures all
  // selected dragstart positions in one ref and uses absolute math
  // (`start + (dragNode.now - dragNode.start)`) so the math can't drift.
  const wasMultiDragRef = useRef(false);

  const handleDragStart = (e) => {
    if (e.evt?.altKey && onAltDuplicate) onAltDuplicate(layer.id);
    if (isMultiSelected && onMultiDragStart) {
      wasMultiDragRef.current = true;
      onMultiDragStart(layer.id);
    } else {
      wasMultiDragRef.current = false;
    }
  };

  const handleDragMove = () => {
    if (!wasMultiDragRef.current || !onMultiDragMove) return;
    const node = groupRef.current;
    onMultiDragMove(layer.id, node.x(), node.y());
  };

  const handleDragEnd = (e) => {
    if (wasMultiDragRef.current) {
      wasMultiDragRef.current = false;
      if (onMultiDragEnd) onMultiDragEnd();
      return;
    }
    onChange({ x: e.target.x(), y: e.target.y() });
  };

  const handleTransformEnd = (e) => {
    const node = e.target;
    let newScaleX = node.scaleX();
    let newScaleY = node.scaleY();
    if (t.mirrorH) newScaleX = -newScaleX;
    if (t.mirrorV) newScaleY = -newScaleY;
    onChange({
      x: node.x(),
      y: node.y(),
      rotation: node.rotation(),
      scaleX: Math.max(0.01, Math.abs(newScaleX)) * (newScaleX < 0 ? -1 : 1),
      scaleY: Math.max(0.01, Math.abs(newScaleY)) * (newScaleY < 0 ? -1 : 1),
    });
  };

  return (
    <Group
      ref={groupRef}
      x={t.x}
      y={t.y}
      rotation={t.rotation}
      scaleX={renderScaleX}
      scaleY={renderScaleY}
      offsetX={cx}
      offsetY={cy}
      visible={!layer.hidden}
      // Locked layers stop responding to clicks/drag from the canvas;
      // they're still selectable from the panel and right-clickable
      // there, where the user can unlock them. Hidden layers don't
      // listen at all.
      listening={!layer.hidden && !layer.locked}
      draggable={!layer.hidden && !layer.locked}
      onClick={onSelect}
      onTap={onSelect}
      onContextMenu={onContextMenu}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onTransformEnd={handleTransformEnd}
    >
      {layer.members.map((m, i) => (
        <KonvaImage
          key={`${m.segmentId}-${i}`}
          image={images[i] || null}
          x={m.dx}
          y={m.dy}
          width={m.w}
          height={m.h}
          listening={true}
          imageSmoothingEnabled={false}
        />
      ))}
    </Group>
  );
}
